import { useMemo, useState } from 'react';
import type { Severity } from '@/lib/postSimulator';
import type { HookResult } from '@/lib/hookAnalyzer';
import { HOOK_TYPES, analyzeHook, reportText, toSegments } from '@/lib/hookAnalyzer';

const SEVERITY_LABEL: Record<Severity, string> = {
  problem: 'À corriger',
  warning: 'À surveiller',
  ok: 'OK',
};

const SAMPLE_A = `Aujourd'hui je voulais partager avec vous une réflexion sur la façon dont nous avons géré notre communication LinkedIn cette année.

Résultat : 3 clients en 6 semaines.`;

const SAMPLE_B = `On a arrêté de poster tous les jours. 3 clients en 6 semaines.

Voici ce qui a changé :`;

function HookCard({ result, title }: { result: HookResult; title?: string }) {
  const [copied, setCopied] = useState(false);
  const segments = toSegments(result);
  const info = HOOK_TYPES[result.type];

  const copy = () => {
    try {
      navigator.clipboard
        .writeText(reportText(result))
        .then(() => setCopied(true))
        .catch(() => {
          // Écriture refusée par le navigateur : le bouton reste inerte.
        });
    } catch {
      // Pas de Clipboard API : on n'interrompt rien.
    }
  };

  return (
    <div className="nz-hook__card">
      {title ? <h3 className="nz-hook__card-title">{title}</h3> : null}
      <div className="nz-hook__verdict">
        <span className="nz-hook__score">
          {result.passed}/{result.total}
        </span>
        <span>
          <strong>{info.label}</strong>
          <small>contrôles passés avant « voir plus »</small>
        </span>
      </div>

      <div className="nz-sim__feed nz-sim__feed--mobile">
        <p className="nz-sim__text">
          {segments.map((segment, index) => {
            if (segment.hidden) {
              return (
                <span key={index} className="nz-hook__hidden">
                  {segment.text}
                </span>
              );
            }
            const cls = segment.kind === 'plain' ? undefined : `nz-hook__mark nz-hook__mark--${segment.kind}`;
            const next = segments[index + 1];
            const endsVisible = result.truncated && next?.hidden;
            return (
              <span key={index}>
                <span className={cls}>{segment.text}</span>
                {endsVisible ? <span className="nz-sim__more">{' '}…voir plus</span> : null}
              </span>
            );
          })}
        </p>
      </div>
      <p className="nz-sim__note">
        Surligné en bleu : ce qui sert l'accroche. Souligné : ce qui la dessert. Texte grisé : masqué sur mobile
        (coupure estimée).
      </p>

      <p className="nz-hook__type">{info.needs}</p>

      {result.rewrite ? (
        <div className="nz-hook__rewrite">
          <span className="nz-sim__badge">Début de phrase proposé</span>
          <p>« {result.rewrite} »</p>
          <small>Tiré de votre propre texte, sans l'ouverture de politesse.</small>
        </div>
      ) : null}

      <ul className="nz-sim__findings">
        {result.checks.map((check) => (
          <li key={check.id} className={`nz-sim__finding nz-sim__finding--${check.severity}`}>
            <span className="nz-sim__badge">{SEVERITY_LABEL[check.severity]}</span>
            <strong>{check.title}</strong>
            <p>{check.detail}</p>
            {check.fix ? <p className="nz-sim__fix">→ {check.fix}</p> : null}
          </li>
        ))}
      </ul>
      <div className="nz-wizard__actions">
        <button type="button" className="nz-btn-secondary" onClick={copy}>
          {copied ? 'Copié' : "Copier l'analyse"}
        </button>
      </div>
    </div>
  );
}

export function HookAnalyzer() {
  const [a, setA] = useState('');
  const [b, setB] = useState('');
  const [compare, setCompare] = useState(false);
  const [ran, setRan] = useState(false);

  const resultA = useMemo(() => (ran && a.trim() ? analyzeHook(a) : null), [ran, a]);
  const resultB = useMemo(() => (ran && compare && b.trim() ? analyzeHook(b) : null), [ran, compare, b]);

  const edit = (setter: (v: string) => void) => (value: string) => {
    setter(value);
    setRan(false);
  };

  const loadSample = () => {
    setA(SAMPLE_A);
    setB(SAMPLE_B);
    setCompare(true);
    setRan(true);
  };

  const clear = () => {
    setA('');
    setB('');
    setCompare(false);
    setRan(false);
  };

  const winner =
    resultA && resultB
      ? resultA.passed === resultB.passed
        ? 'Égalité sur les contrôles : départagez à la lecture, à voix haute.'
        : `${resultA.passed > resultB.passed ? 'A' : 'B'} passe ${Math.abs(resultA.passed - resultB.passed)} contrôle${Math.abs(resultA.passed - resultB.passed) > 1 ? 's' : ''} de plus.`
      : null;

  return (
    <div className="nz-sim nz-hook">
      <label className="nz-sim__label" htmlFor="nz-hook-a">
        {compare ? 'Accroche A' : 'Collez vos premières lignes'}
      </label>
      <textarea
        id="nz-hook-a"
        className="nz-sim__input"
        rows={5}
        value={a}
        placeholder="Les 2 ou 3 premières lignes de votre post, telles que vous les publieriez."
        onChange={(event) => edit(setA)(event.target.value)}
      />
      {compare ? (
        <>
          <label className="nz-sim__label nz-hook__label-b" htmlFor="nz-hook-b">
            Accroche B
          </label>
          <textarea
            id="nz-hook-b"
            className="nz-sim__input"
            rows={5}
            value={b}
            placeholder="Une seconde version à comparer."
            onChange={(event) => edit(setB)(event.target.value)}
          />
        </>
      ) : null}

      <div className="nz-sim__bar">
        <button type="button" className="nz-btn-primary" disabled={!a.trim()} onClick={() => setRan(true)}>
          Analyser l'accroche
        </button>
        <button
          type="button"
          className="nz-wizard__back"
          onClick={() => {
            setCompare(!compare);
            setRan(false);
          }}
        >
          {compare ? 'Une seule accroche' : 'Comparer deux versions'}
        </button>
        <button type="button" className="nz-wizard__back nz-sim__sample" onClick={a || b ? clear : loadSample}>
          {a || b ? 'Effacer' : 'Essayer avec un exemple'}
        </button>
      </div>

      {resultA ? (
        <div className="nz-sim__result" aria-live="polite">
          {winner ? <h2 className="nz-sim__summary">{winner}</h2> : null}
          <div className={resultB ? 'nz-hook__cols' : undefined}>
            <HookCard result={resultA} title={resultB ? 'Accroche A' : undefined} />
            {resultB ? <HookCard result={resultB} title="Accroche B" /> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
